<template>
  <div class="bills-registry-created">
    <loader
      class="bills-registry-created__loader"
      v-if="isRegistryProcessing || isRegistryPositionDeleting"
    ></loader>
    <transactions-page-controls
      :has-stats="false"
      :has-export="false"
      :has-refresh="true"
      :has-filter="false"
      :bills="true"
      @refresh="handleRefresh"
      :stats="registryStats"
    >
      <template #specificActions>
        <primary-button
          class="bills-registry-created__action"
          :label="$t('bills.by-registry.created.actions.process-registry')"
          :disabled="hasWarning || isRequestProcessing"
          @click="processBillsRegistry"
        ></primary-button>
      </template>
    </transactions-page-controls>
    <div class="bills-registry-warning" v-if="showErrorsWarning">
      <div class="bills-registry-warning__icon">
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M0 7.5C0 3.35786 3.35786 0 7.5 0C11.6421 0 15 3.35786 15 7.5C15 11.6421 11.6421 15 7.5 15C3.35786 15 0 11.6421 0 7.5Z"
            fill="#FDE9E9"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M7.49982 9.54541C7.87638 9.54541 8.18164 9.78962 8.18164 10.0909L8.18164 10.3636C8.18164 10.6648 7.87638 10.909 7.49982 10.909C7.12326 10.909 6.818 10.6648 6.818 10.3636L6.818 10.0909C6.818 9.78962 7.12326 9.54541 7.49982 9.54541Z"
            fill="#DD6262"
          />
          <path
            d="M6.82407 3.62199C6.77146 3.14756 7.08904 2.72729 7.50018 2.72729C7.91131 2.72729 8.2289 3.14756 8.17628 3.62199L7.69242 7.98517C7.66334 8.2474 7.33702 8.2474 7.30794 7.98517L6.82407 3.62199Z"
            fill="#DD6262"
          />
        </svg>
      </div>
      <div class="bills-registry-warning__content">
        <div
          class="bills-registry-warning__close"
          @click.stop="hideErrorsWarning"
        >
          {{ $t('bills.by-registry.created.warning.close') }}
        </div>
        <div class="bills-registry-warning__title">
          {{ $t('bills.by-registry.created.warning.title') }}
        </div>
        <div class="bills-registry-warning__text">
          {{ $t('bills.by-registry.created.warning.text') }}
        </div>
        <div class="bills-registry-warning__rows">
          <div class="bills-registry-warning__rows-title">
            {{
              $t('bills.by-registry.created.warning.rows-title', {
                count: errorsRows.length,
              })
            }}
          </div>
          <div class="bills-registry-warning__rows-list">
            <div
              class="bills-registry-warning__rows-item"
              v-for="(errorRow, index) in errorsRows"
              :key="`error-row-${index}`"
            >
              {{ errorRow }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <bills-registry-created-table
      class="bills-registry-created__table"
      :bills-registry-items="billsRegistryPositionsList || []"
      @registryPositionUpdated="updateBillsRegistryItem"
      @registryPositionDeleted="deleteBillsRegistryItem"
    ></bills-registry-created-table>
    <bills-registry-item-delete-popup
      ref="billsRegistryItemDeleteConfirmPopup"
    ></bills-registry-item-delete-popup>
    <success-notification
      ref="billsRegistryProcessingNotification"
      :text="$t('bills.by-registry.created.notifications.registry-in-progress')"
    ></success-notification>
  </div>
</template>

<script>
import {$emit} from '../../../../utils/gogocodeTransfer'
import Loader from '@vue-app/components/base/Loader.vue'
import {CompaniesApi} from '@js-app/office/api/companies-api'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import TransactionsPageControls
  from '@vue-app/components/company/transactions/shared/transactions-page-controls/TransactionsPageControls.vue'
import RequireAccess from '@vue-app/components/base/role-based/RequireAccess.vue'
import {Stats} from '@js-app/entities/transaction/Stats'
import TransactionsTable from '@vue-app/components/company/transactions/table/TransactionsTable.vue'
import BillsRegistryCreatedTable
  from '@vue-app/components/company/bills/by-registry/created/table/BillsRegistryCreatedTable.vue'
import BillsRegistryItemDeletePopup from '@vue-app/components/company/bills/popup/BillsRegistryItemDeletePopup.vue'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'
import {mapState} from 'vuex'

export default {
  name: 'BillRegistryCreated',
  components: {
    Loader,
    PrimaryButton,
    TransactionsTable,
    TransactionsPageControls,
    RequireAccess,
    BillsRegistryCreatedTable,
    BillsRegistryItemDeletePopup,
    SuccessNotification,
  },
  props: {
    billsRegistry: {
      type: [Object, Array],
    },
    billsRegistryPositions: {
      type: [Object, Array],
    },
  },
  data() {
    return {
      isRegistryProcessing: false,
      isRequestProcessing: false,
      isRegistryPositionDeleting: false,
      isRegistryPositionsLoading: false,
      billsRegistryPositionsList: [],
      showErrorsWarning: false,
      refreshKey: 0,
    }
  },
  computed: {
    ...mapState(['selectedCompany']),
    registryStats() {

      return new Stats({
        OperationsStats: {
          All: {
            Sum: this.billsRegistryPositions
              .map((val) => {
                return val.sum || 0
              })
              .reduce((total, val) => total + val, 0),
            Count: this.billsRegistry.total_count,
          },
        },
      })
    },

    hasWarning() {
      const hasErrors = this.billsRegistryPositions.some((position) => {
        return Object.values(position.errors).length > 0
      })

      this.showErrorsWarning = hasErrors
      return hasErrors
    },

    errorsRows() {
      let _errorsRows = []

      if (!this.hasWarning) {
        return _errorsRows
      }

      this.billsRegistryPositions.forEach((position) => {
        const errors = Object.values(position.errors)
        if (Array.isArray(errors)) {
          errors.forEach((_error, index) => {
            let _billsRegistryItem = this.billsRegistryPositionsList[index]

            if (_billsRegistryItem) {
              _errorsRows.push(
                `${_billsRegistryItem.order_number};${_billsRegistryItem.purpose};${_billsRegistryItem.recipient_email};${_billsRegistryItem.recipient_name};${_billsRegistryItem.recipient_phone};${_billsRegistryItem.sum}`
              )
            }
          })
        }
      })

      return _errorsRows
    },
  },
  created() {
    this.billsRegistryPositionsList = this.billsRegistryPositions || []
  },
  methods: {
    async getUpdatedBillRegistryItemById(
      companyId,
      registryId,
      registryItemId
    ) {
      try {
        return (
          (
            await CompaniesApi.getBillsRegistryItemById(
              companyId,
              registryId,
              registryItemId
            )
          ).data || {}
        )
      } catch (error) {
        console.error(`Something went wrong: ${error}`)
      }
    },

    async updateBillsRegistryItem(registryPositionItem) {
      this.isRequestProcessing = true

      const rawSum = String(registryPositionItem.sum ?? '0')

      const normalizedSum = parseFloat(
          rawSum.replace(/\s+/g, '').replace(',', '.')
      )

      this.billsRegistryPositionsList.find(
          (_item) => _item.id === registryPositionItem.id
      ).sum = normalizedSum

      let index = this.billsRegistryPositionsList.findIndex(
          (_item) => _item.id === registryPositionItem.id
      )

      this.refreshKey++

      await CompaniesApi.updateBillsRegistryById(
          this.selectedCompany,
          registryPositionItem.registry_id,
          registryPositionItem.id,
          {
            recipient_name: registryPositionItem.recipient_name,
            recipient_phone: registryPositionItem.recipient_phone,
            recipient_email: registryPositionItem.recipient_email,
            order_number: registryPositionItem.order_number,
            purpose: registryPositionItem.purpose,
            sum: normalizedSum,
          }
      )

      this.billsRegistryPositionsList[index] = await this.getUpdatedBillRegistryItemById(
          this.selectedCompany,
          registryPositionItem.registry_id,
          registryPositionItem.id
      )

      this.isRequestProcessing = false
    },

    async deleteBillsRegistryItem(registryPositionItem) {
      let isConfirm =
        await this.$refs.billsRegistryItemDeleteConfirmPopup.open()

      if (!isConfirm) {
        return
      }

      this.isRegistryPositionDeleting = true

      try {
        await CompaniesApi.deleteBillsRegistryById(
          this.selectedCompany,
          registryPositionItem.registry_id,
          registryPositionItem.id
        )
        this.billsRegistryPositionsList.splice(
          this.billsRegistryPositionsList.findIndex(
            (_position) =>
              parseInt(_position.id) === parseInt(registryPositionItem.id)
          ),
          1
        )
      } finally {
        this.isRegistryPositionDeleting = false
      }
    },

    async processBillsRegistry() {
      this.isRegistryProcessing = true
      try {
        this.$refs.billsRegistryProcessingNotification.show()

        await CompaniesApi.processBillsRegistry(
          this.selectedCompany,
          this.billsRegistry.id
        )
        window.location.reload()
      } finally {
        this.isRegistryProcessing = false
      }
    },

    handleRefresh() {
      $emit(this, 'refresh')
    },

    hideErrorsWarning() {
      this.showErrorsWarning = false
    },
  },
  emits: ['refresh'],
}
</script>

<style lang="scss" scoped>
.bills-registry-created {
  position: relative;
  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 20%;
    height: 100%;
    z-index: 99;
  }

  &__table {
    margin-top: 21px;
  }
}
.bills-registry-warning {
  padding: 12px 20px 30px;
  background: #fff2f2;
  border-radius: 18px;
  margin: 15px 0 10px;
  display: grid;
  grid-template-columns: 30px 1fr;
  column-gap: 24px;
  &__content {
    position: relative;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: #fffafa;
    border-radius: 24px;
  }

  &__title {
    font-weight: 600;
    font-size: 12px;
    line-height: 20px;
    color: #dd6262;
    margin-bottom: 3px;
  }

  &__text {
    font-weight: 500;
    font-size: 11px;
    line-height: 15px;
    color: #585f6d;
  }

  &__close {
    position: absolute;
    top: 0;
    right: 20px;
    font-weight: 600;
    font-size: 14px;
    line-height: 17px;
    color: #64748B;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }

  &__rows {
    margin-top: 10px;

    &-title,
    &-item {
      font-weight: 400;
      font-size: 11px;
      line-height: 15px;
      color: #585f6d;
    }
  }
}
.bills-registry-created__loader {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0 auto;
}
</style>
